'use client';

import type { HTMLAttributes } from 'react';
import React from 'react';
import Highlighter from 'react-highlight-words';
import {
  normalizeClipboardData,
  truncateText,
} from 'components/TextWithTruncation/utils';
import styled, { css } from 'styled-components';
import { getTextColor } from 'theme-utils';
import invariant from 'tiny-invariant';
import type { AddDollarPrefix } from 'utils/types';
import isValidProp from '@emotion/is-prop-valid';
import { theme } from '@frameio/vapor-theme';
import { textStyles } from './text-variants';
import { TextWithTooltip } from './TextWithTooltip';
import type {
  TextAlign,
  TextDisplay,
  TextSupportedColorTokens,
  TextTruncation,
  TextVariantNumeric,
  TextVariants,
  TextWeights,
} from './types';
import { isMiddleTruncation } from './utils';

export type TextProps = HTMLAttributes<HTMLSpanElement> & {
  /** Alter the element with which to render the Text. Defaults to `span` */
  as?: any;
  /** Text to highlight. Only works when children of `Text` is also a string. */
  highlight?: string[] | string;
  /**
   * Enable a tooltip to display when the user hovers over the text and it
   * is overflowing.
   */
  enableTooltip?: boolean;
  /**
   * The typography variant to use, which alters the font-size and line-height.
   */
  variant?: TextVariants;
  /**
   * The type of truncation logic to use. If undefined, no truncation
   * will be applied.
   */
  truncate?: TextTruncation;
  /**
   * The color of the text to use. You must specify `inherit` if you wish to
   * inherit the color of the parent element.
   */
  color?: TextSupportedColorTokens | 'inherit';
  /**
   * Alternatively use inline, or inline-block to display the text.
   */
  display?: TextDisplay;
  /** Specify the text alignment. */
  textAlign?: TextAlign;
  /** Specify the font-weight */
  fontWeight?: TextWeights;
  /**
   * Please ensure the correct font assets are configured before using this option
   */
  italic?: boolean;
  /**
   * Specify if the numers should be rendered using tabular nums. You should
   * use this option any time numbers change frequently, such as with a progress
   * indicator.
   */
  variantNumeric?: TextVariantNumeric;
};

/**
 * The basic primitive component for rendering Text using vapor predefined
 * styles. [View component documentation](https://vapor.frame.io/vapor/components/text).
 *
 * Basic usage:
 *
 * ```jsx
 * <Text variant='body' color='primary'>Primary text</Text>
 * ```
 *
 * Enabling tooltips and truncation:
 *
 * ```jsx
 * <Text enableTooltip truncate>Text that will be truncated and have a tooltip</Text>
 * ```
 *
 * Text styles can also be used as a styled-components mixin:
 *
 * ```jsx
 * import { textStyles } from '@frameio/vapor';
 *
 * const MyText = styled.div`
 *   ${textStyles.body};
 *   color: ${theme.colors.text.primary};
 * `
 * ```
 */

export const Text = React.forwardRef(
  (
    {
      display = 'block',
      truncate,
      color = 'body',
      fontWeight,
      variant = 'primary',
      enableTooltip = false,
      textAlign,
      highlight,
      ...other
    }: TextProps,
    ref: React.Ref<any>
  ) => {
    const textNode = (
      <TextNode
        ref={ref}
        color={color}
        display={display}
        fontWeight={fontWeight}
        highlight={highlight}
        textAlign={textAlign}
        truncate={truncate}
        variant={variant}
        {...other}
      />
    );

    if (enableTooltip && truncate) {
      return <TextWithTooltip truncate={truncate}>{textNode}</TextWithTooltip>;
    }

    return textNode;
  }
);

/**
 * Text node responsive for rendering either middle truncated text,
 * regular text, or a highlighted text.
 */

const TextNode = React.forwardRef(
  (
    {
      children,
      color,
      display,
      highlight,
      textAlign,
      variant,
      fontWeight,
      truncate,
      variantNumeric,
      italic,
      ...other
    }: TextProps,
    ref: React.Ref<any>
  ) => {
    const textNodeProps = {
      $color: color,
      $display: display,
      $fontWeight: fontWeight,
      $truncate: truncate,
      $textAlign: textAlign,
      $variant: variant,
      $variantNumeric: variantNumeric,
      $italic: italic,
      ...other,
    };

    if (isMiddleTruncation(truncate)) {
      invariant(
        typeof children === 'string',
        'Middle truncation requires that the child element is a string'
      );

      invariant(!highlight, 'Middle truncation does not support highlighting');

      const { initial, remainder } = truncateText({
        hasExtension: truncate.hasExtension,
        padding: truncate.padding,
        text: children,
      });

      return (
        <StyledText {...textNodeProps} onCopy={normalizeClipboardData}>
          <TruncatedSpan ref={ref}>{initial}</TruncatedSpan>
          <TruncatedSpanRemaining>{remainder}</TruncatedSpanRemaining>
        </StyledText>
      );
    }

    let text = children;

    if (highlight && typeof text !== 'undefined') {
      invariant(
        typeof text === 'string',
        'children of Text must be a string to support highlighting'
      );

      text = (
        <Highlighter
          autoEscape={true}
          highlightClassName="highlight"
          searchWords={Array.isArray(highlight) ? highlight : [highlight]}
          textToHighlight={text}
        />
      );
    }

    return (
      <StyledText ref={ref} {...textNodeProps}>
        {text}
      </StyledText>
    );
  }
);

/**
 * Text styles
 */

type StyledTextProps = Required<
  Pick<
    AddDollarPrefix<TextProps>,
    | '$variant'
    | '$truncate'
    | '$color'
    | '$display'
    | '$textAlign'
    | '$fontWeight'
    | '$variantNumeric'
    | '$italic'
  >
>;

export const truncateStyles = css`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const highlightStyles = css`
  background-color: ${theme.colors.background.interactive.highlight};
  color: inherit;
`;

const lineClamp = (lines: number) => css`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${lines};
  display: -webkit-box;
  overflow: hidden;
  overflow-wrap: break-word; // https://frame-io.atlassian.net/browse/VAPR-2535 -- allows for long words to wrap to the next line
`;

const middleTruncationStyles = css`
  ${truncateStyles};
  display: flex;
  min-width: 0;
`;

const getTruncationStyles = (p: StyledTextProps) => {
  // middle truncation
  if (isMiddleTruncation(p.$truncate)) {
    return middleTruncationStyles;
  }

  // regular single-line end truncation
  if (p.$truncate === true) {
    return css`
    ${truncateStyles}

    /**
    * Safari Hack:
    * Prevent native tooltip from showing when text is truncated.
    * https://shorturl.at/uBRX8
    */
    &::after {
      content: '';
      display: block;
    }
    `;
  }

  // multi line truncation using line-clamp
  if (typeof p.$truncate === 'number') {
    return lineClamp(p.$truncate);
  }

  return css``;
};

const StyledText = styled.span.withConfig({
  shouldForwardProp: (prop) => isValidProp(prop),
})<StyledTextProps>`
  color: ${(p) => {
    if (p.$color === 'inherit') {
      return 'inherit';
    }

    return getTextColor(p.$color) ?? p.$color;
  }};
  display: ${(p) => p.$display};
  ${(p) => textStyles[p.$variant]};
  font-variant-numeric: ${(p) => p.$variantNumeric};
  font-weight: ${(p) => theme.fontWeights[p.$fontWeight]};
  text-align: ${(p) => p.$textAlign};
  font-style: ${(p) => (p.$italic ? 'italic' : null)};
  & mark.highlight {
    ${highlightStyles};
  }
  ${(p) => getTruncationStyles(p)};
`;

const TruncatedSpan = styled.span`
  ${truncateStyles}
  white-space: pre;
`;

const TruncatedSpanRemaining = styled.span`
  white-space: pre;
`;
